import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { PageShell, SectionLabel, SlideCorner } from "@/components/Layout";
import { toast } from "sonner";
import { MapPin, Instagram, Globe, Share2, ArrowLeft, Sparkles, ArrowRight } from "lucide-react";
import { disciplineColor } from "@/lib/disciplines";

export default function ArtistProfile() {
  const { slug } = useParams();
  const [artist, setArtist] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setArtist(null); setNotFound(false);
    api.get(`/artist/${slug}`).then(({ data }) => setArtist(data)).catch(() => setNotFound(true));
  }, [slug]);

  const share = async () => {
    const url = window.location.href;
    if (navigator.share) {
      try { await navigator.share({ title: `${artist.name} · anArtizen`, url }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); } catch { toast.error("Could not share"); }
  };

  if (notFound) return (
    <PageShell>
      <div className="max-w-[800px] mx-auto px-5 md:px-10 py-32 text-center">
        <SectionLabel>404</SectionLabel>
        <h1 className="font-display text-5xl md:text-6xl font-bold leading-tight">Artist <span className="hl-cyan">not found.</span></h1>
        <p className="mt-6 text-white/70">They may have left, or this profile isn't public yet.</p>
        <Link to="/discovery" className="btn-pill mt-8 inline-flex">Back to discovery <ArrowRight size={14} /></Link>
      </div>
    </PageShell>
  );

  if (!artist) return <PageShell><div className="p-20 font-mono text-sm">Loading…</div></PageShell>;

  const color = disciplineColor(artist.discipline);

  return (
    <PageShell>
      <section className="relative grid-bg border-b border-border" style={{ borderTop: `4px solid ${color}` }}>
        <SlideCorner right={`PROFILE · ${artist.discipline?.toUpperCase().replace("_", " ")}`} />
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-20 pb-12">
          <Link to="/discovery" className="meta-mono inline-flex items-center gap-2 mb-8 hover:text-primary"><ArrowLeft size={11} /> BACK TO DISCOVERY</Link>
          <div className="grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="aspect-[4/5] bg-muted overflow-hidden border border-border" style={{ borderColor: color }}>
                {artist.profile_image_url ? (
                  <img src={artist.profile_image_url} alt={artist.name} className="w-full h-full object-cover" />
                ) : <div className="w-full h-full grid place-items-center text-white/20 font-display text-9xl">{artist.name?.[0]}</div>}
              </div>
            </div>
            <div className="lg:col-span-7 flex flex-col">
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] flex items-center gap-2 mb-6" style={{ color }}>
                <span style={{ width: 22, height: 1, background: color, display: "inline-block" }} />
                {artist.discipline?.replace("_", " ")} · {artist.city}
              </p>
              <h1 className="font-display text-5xl md:text-7xl lg:text-[7.5rem] font-bold leading-[0.9] tracking-tighter">{artist.name}</h1>
              {artist.aesthetic && <p className="font-serif-italic text-2xl md:text-3xl text-white/85 mt-6">{artist.aesthetic}</p>}
              {artist.bio && <p className="mt-8 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">{artist.bio}</p>}
              <div className="mt-10 flex flex-wrap gap-3">
                <button data-testid="profile-share" onClick={share} className="btn-pill btn-coral"><Share2 size={14} /> Share profile</button>
                <Link to="/brands#brief" data-testid="profile-book" className="btn-pill">Brief us for this artist <ArrowRight size={14} /></Link>
              </div>
              <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-px bg-border">
                <Meta k="Discipline" v={artist.discipline?.replace("_", " ")} />
                <Meta k="Based in" v={artist.city} icon={<MapPin size={11} />} />
                {artist.verified && <Meta k="Status" v="Verified" icon={<Sparkles size={11} />} />}
                {artist.instagram && <Meta k="Instagram" v={artist.instagram} icon={<Instagram size={11} />} />}
                {artist.website && <Meta k="Web" v={artist.website} icon={<Globe size={11} />} />}
                {artist.rate_card && <Meta k="Rate card" v={artist.rate_card} />}
              </div>
            </div>
          </div>
        </div>
      </section>

      {artist.portfolio_urls?.length > 0 && (
        <section className="border-b border-border">
          <div className="max-w-[1600px] mx-auto px-5 md:px-10 py-16">
            <SectionLabel>Selected Work</SectionLabel>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-border">
              {artist.portfolio_urls.map((u) => (
                <div key={u} className="bg-background"><img src={u} alt="" className="w-full aspect-square object-cover" /></div>
              ))}
            </div>
          </div>
        </section>
      )}

      <section>
        <div className="max-w-[1100px] mx-auto px-5 md:px-10 py-20">
          <p className="font-serif-italic text-2xl md:text-4xl text-white/85 leading-snug">
            Want to work with {artist.name?.split(" ")[0]}? <Link to="/brands#brief" className="hl-coral not-italic font-display font-bold">Send a brief</Link> and we'll start the conversation within 48 hours.
          </p>
        </div>
      </section>
    </PageShell>
  );
}

function Meta({ k, v, icon }) {
  return (
    <div className="bg-background p-4">
      <p className="meta-mono mb-2 flex items-center gap-1.5">{icon} {k}</p>
      <p className="font-mono text-sm text-white/90 break-words">{v}</p>
    </div>
  );
}
